/**
 * VIP waitlist signup.
 *
 * Posts to the same Klaviyo client subscriptions endpoint and the same list as
 * zoetsnacks.com, so signups from the storefront land in one audience. The
 * endpoint is browser safe: it authenticates with the public Site ID, so there is
 * no private key here and no backend needed.
 *
 * Progressive enhancement. The markup is a real Shopify customer form that works
 * with JavaScript off, capturing the address to the store's customer list. When
 * this module loads it takes over submission and sends to Klaviyo instead.
 *
 * Two steps on purpose: the email is subscribed the moment it is entered, so a
 * visitor who abandons the optional second step is still on the list. Klaviyo
 * upserts by email, so step two enriches the same profile.
 */
const JOINED_COOKIE = 'zoet_waitlist';

// Name of the customer event published on a step one signup. The custom pixel in
// Settings > Customer events subscribes to this exact string — change one and you
// must change the other. See docs/meta-vip-lead-pixel.md.
const LEAD_EVENT = 'vip_waitlist_signup';
const LEAD_SUBMIT_EVENT = 'generate_lead';
const DETAILS_SUBMIT_EVENT = 'vip_signup_details_submit';
const DETAILS_SKIP_EVENT = 'vip_signup_details_skip';
const META_PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

function hasJoined() {
  return document.cookie.split('; ').some((entry) => entry.startsWith(`${JOINED_COOKIE}=`));
}

function setJoined() {
  // Matches the cookie zoetsnacks.com sets, so someone who joined there is not
  // asked again here.
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${JOINED_COOKIE}=1; path=/; max-age=${oneYear}; samesite=lax`;
}

/** Shared by the Meta Lead and the published event so the two can be deduplicated. */
function newEventId() {
  try {
    return crypto.randomUUID();
  } catch (error) {
    return `vip-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

/**
 * Whether Shopify's consent API says this visitor may be tracked.
 *
 * The sandboxed pixels get this gating for free; sending from the page means
 * asking ourselves. A store with no consent banner has no restriction to apply
 * and the API may be absent entirely, which is why the default is to allow.
 */
function canBeTracked() {
  try {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.userCanBeTracked !== 'function') return true;
    return privacy.userCanBeTracked();
  } catch (error) {
    return true;
  }
}

/**
 * Fallback loader for Meta's library, minus the PageView it normally sends.
 *
 * Usually a no-op: the Facebook & Instagram channel's pixel is configured with
 * runtimeContext OPEN, so Shopify runs it in the page rather than in the web
 * pixel sandbox, and `window.fbq` is already there and initialised with this
 * pixel id by the time anyone submits. This only does anything if the channel's
 * script has not landed yet, or the channel is removed. Either way there is only
 * ever one copy — Shopify's snippet bails on an existing `window.fbq` exactly as
 * this one does.
 */
function loadMetaPixel(pixelId) {
  if (window.fbq) return;

  const fbq = function () {
    fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = META_PIXEL_SRC;
  document.head.appendChild(script);

  fbq('init', pixelId);
}

class ZoetVipSignup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-vip-form]');
    this.emailStep = this.querySelector('[data-vip-step="email"]');
    this.detailStep = this.querySelector('[data-vip-step="details"]');
    this.doneStep = this.querySelector('[data-vip-step="done"]');
    this.status = this.querySelector('[data-vip-status]');
    this.detailForm = this.querySelector('[data-vip-details]');

    if (!this.form) return;

    this.companyId = this.dataset.companyId;
    this.listId = this.dataset.listId;

    // Without both ids the Klaviyo call cannot succeed, so leave the native
    // Shopify form in place rather than intercepting into a guaranteed failure.
    if (!this.companyId || !this.listId) return;

    this.takeOverForm();
    this.form.addEventListener('submit', this.handleEmail.bind(this));
    if (this.detailForm) {
      this.detailForm.addEventListener('submit', this.handleDetails.bind(this));
    }
    this.querySelectorAll('[data-vip-skip]').forEach((el) =>
      el.addEventListener('click', () => {
        if (this.detailsSubmitting) return;
        if (!this.detailsOutcomeTracked) {
          this.detailsOutcomeTracked = true;
          this.trackGoogleEvent(DETAILS_SKIP_EVENT, { form_step: 'details' });
        }
        this.showStep('done');
      })
    );

    // Already joined on a previous visit, or on zoetsnacks.com: skip straight to
    // the confirmation rather than asking for the same email twice.
    if (hasJoined()) this.showStep('done');
  }

  /**
   * Hands step one's form over to this component.
   *
   * Shopify binds its storefront spam-protection script to every
   * `{% form 'customer' %}`. Once that script has an hCaptcha token it calls
   * `form.submit()` directly — a native submit, which fires no submit event and
   * so cannot be stopped by the preventDefault() in handleEmail. The page posted
   * to /contact and navigated away just as step two appeared, and the visitor
   * came back to a reloaded page that sent them straight to the confirmation.
   *
   * `form_type` is the input that marks a form as one the captcha protects, so
   * dropping it stops the script binding at all; shadowing submit() makes a
   * native submit a no-op even if something else binds later. Neither runs when
   * this component bails out above, so the no-JS fallback still posts to Shopify
   * exactly as before.
   */
  takeOverForm() {
    this.form.submit = () => {};
    this.form
      .querySelectorAll('input[name="form_type"], input[name="utf8"]')
      .forEach((input) => input.remove());
  }

  /**
   * Announces a step one signup on Shopify's customer event bus.
   *
   * The store's Meta pixel is installed through the Facebook & Instagram channel,
   * so it runs inside Shopify's web pixel sandbox and window.fbq is not reachable
   * from theme code. Publishing is the supported way across that boundary: a
   * custom pixel subscribes to LEAD_EVENT and forwards it to Meta as a Lead.
   * docs/meta-vip-lead-pixel.md carries that pixel's code and how to install it.
   *
   * Published only after the Klaviyo subscribe resolves, so a Lead always means
   * an address that actually reached the list. Wrapped because analytics must
   * never be able to break the signup.
   */
  publishLead(email, eventId) {
    try {
      window.Shopify?.analytics?.publish(LEAD_EVENT, {
        email,
        event_id: eventId,
        source: this.dataset.source || 'zoet-theme-vip-page',
        list_id: this.listId,
      });
    } catch (error) {
      console.error('VIP signup event failed to publish:', error);
    }
  }

  /**
   * Sends a non-PII event through the Google tag, or queues it for GTM when the
   * tag is not exposed as `gtag`. The tag's consent mode remains authoritative.
   */
  trackGoogleEvent(name, parameters = {}) {
    const eventParameters = {
      form_id: this.form.id,
      form_name: 'vip_waitlist',
      signup_source: this.dataset.source || 'zoet-theme-vip-page',
      ...parameters,
    };

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, eventParameters);
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...eventParameters });
    } catch (error) {
      console.error('VIP Google event failed:', error);
    }
  }

  /**
   * Sends the Meta Lead for a step one signup.
   *
   * The Facebook & Instagram channel installs the pixel with runtimeContext OPEN,
   * so it runs in the page and `window.fbq` is already present and initialised
   * with this pixel id. This reuses that instance rather than standing up a
   * second one; loadMetaPixel is only a fallback for when it is missing.
   *
   * Two things keep it from corrupting the pixel's data:
   *
   * - No `fbq('track', 'PageView')`. The channel's pixel already sends one on
   *   every page against this same id, and a second would double count.
   * - `eventID` is shared with the published customer event, so if a pixel in
   *   Shopify's sandbox is ever wired up to forward LEAD_EVENT as well, Meta
   *   deduplicates the pair instead of counting the signup twice.
   *
   * A custom pixel subscribing to LEAD_EVENT was tried first and did not produce
   * a Lead; sending from the page is what works.
   */
  trackLead(email, eventId) {
    const pixelId = this.dataset.pixelId;
    if (!pixelId || !canBeTracked()) return;

    try {
      loadMetaPixel(pixelId);
      // Advanced matching. fbq SHA-256s the address in the browser, so the raw
      // value never leaves; re-initing is how matching data gets attached.
      window.fbq('init', pixelId, { em: email });
      window.fbq(
        'track',
        'Lead',
        {
          content_name: 'VIP waitlist',
          content_category: this.dataset.source || 'zoet-theme-vip-page',
        },
        { eventID: eventId }
      );
    } catch (error) {
      // Never let the pixel break a signup.
      console.error('VIP Lead pixel failed:', error);
    }
  }

  showStep(name) {
    [
      ['email', this.emailStep],
      ['details', this.detailStep],
      ['done', this.doneStep],
    ].forEach(([key, el]) => {
      if (el) el.hidden = key !== name;
    });

    // Only once the flow is actually over. Setting this at step one meant any
    // reload part way through dropped the visitor back on the confirmation with
    // no way to answer the rest of the questions.
    if (name === 'done') setJoined();
  }

  setStatus(message, isError) {
    if (!this.status) return;
    this.status.textContent = message || '';
    this.status.dataset.error = isError ? 'true' : 'false';
  }

  async handleEmail(event) {
    event.preventDefault();
    if (this.emailSubmitting) return;

    const input = this.form.querySelector('[data-vip-email]');
    const button = this.form.querySelector('button[type="submit"]');
    const email = (input?.value || '').trim();
    if (!email) return;

    this.emailSubmitting = true;
    button.disabled = true;
    this.setStatus(this.dataset.textSending, false);

    try {
      await this.subscribe({ email });
      this.email = email;
      // One id for both routes, so Meta collapses them if they ever both fire.
      const eventId = newEventId();
      this.publishLead(email, eventId);
      this.trackLead(email, eventId);
      this.trackGoogleEvent(LEAD_SUBMIT_EVENT, {
        form_step: 'email',
        method: 'vip_waitlist',
      });
      this.setStatus('', false);
      this.showStep(this.detailStep ? 'details' : 'done');
      const next = this.querySelector('[data-vip-step="details"] input, [data-vip-step="done"] a');
      if (next) next.focus();
    } catch (error) {
      console.error('VIP signup failed:', error);
      this.setStatus(this.dataset.textError, true);
    } finally {
      this.emailSubmitting = false;
      button.disabled = false;
    }
  }

  async handleDetails(event) {
    event.preventDefault();
    if (this.detailsSubmitting) return;

    const button = this.detailForm.querySelector('button[type="submit"]');
    const phone = (this.detailForm.querySelector('[data-vip-phone]')?.value || '').trim();
    const candy = (this.detailForm.querySelector('[data-vip-candy]')?.value || '').trim();
    const flavors = Array.from(
      this.detailForm.querySelectorAll('[data-vip-flavor]:checked')
    ).map((input) => input.value);

    this.detailsSubmitting = true;
    button.disabled = true;
    this.setStatus(this.dataset.textSending, false);

    try {
      await this.subscribe({ email: this.email, phone, candy, flavors });
      if (!this.detailsOutcomeTracked) {
        this.detailsOutcomeTracked = true;
        this.trackGoogleEvent(DETAILS_SUBMIT_EVENT, { form_step: 'details' });
      }
    } catch (error) {
      // The email is already on the list from step one, so a failure here costs
      // the extras and nothing else. Do not block the confirmation.
      console.error('VIP detail update failed:', error);
    } finally {
      this.detailsSubmitting = false;
      button.disabled = false;
      this.setStatus('', false);
      this.showStep('done');
    }
  }

  /* Best effort US normalisation to E.164. Anything ambiguous is dropped rather
     than guessed, so a malformed number never fails the signup. */
  toE164(raw) {
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (raw.trim().startsWith('+') && digits.length >= 8) return `+${digits}`;
    return null;
  }

  async subscribe({ email, phone, candy, flavors }) {
    const e164 = this.toE164(phone);
    // Property names match the ones zoetsnacks.com already writes, so both
    // sources land on the same profile fields.
    const properties = { signup_source: this.dataset.source || 'zoet-theme-vip-page' };
    if (candy) properties.favorite_candy = candy;
    if (flavors && flavors.length) properties.favorite_flavors = flavors;

    const subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
    if (e164) {
      subscriptions.sms = {
        marketing: { consent: 'SUBSCRIBED' },
        transactional: { consent: 'SUBSCRIBED' },
      };
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Must be recent enough to accept the subscriptions object.
          revision: '2026-04-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.dataset.customSource || 'Zoet VIP List',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    ...(e164 ? { phone_number: e164 } : {}),
                    properties,
                    subscriptions,
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: this.listId } } },
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Klaviyo subscribe failed: ${response.status}`);
    }
  }
}

if (!customElements.get('zoet-vip-signup')) {
  customElements.define('zoet-vip-signup', ZoetVipSignup);
}
